import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { format, formatDistanceToNow } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

import { resetQueries } from '@/utils'

import ImagesContainer from '../../../ImagesContainer/ImagesContainer'
import Icon from '../../../Icons'
import Avatar from '../../../../Avatar'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import DropdownDots from '../../../DropdownDots/DropdownDots'
import ImagesPicker from '../../../ImagesPicker/ImagesPicker'
import { sortByDate } from '../../../../reducers/Organisation/groupsReducer'
import { cutTooLong } from '../../../../helpers/helpers'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import { GET_PENDING_COMMENTS_QUERY_KEY } from '../../../../api/groups/get-pending-comments'

import PostComment from './PostComment/PostComment'

const USER_DELETED = 'User deleted'

const PostSmallCard = ({
  post: {
    id,
    author,
    message,
    title,
    images,
    createdAt,
    comments,
    approved,
    groupId,
    likes,
    isWelcomePost,
    scheduledAt,
    published,
  },
  onDeletePost,
  onEditPost,
  onShowFullPost,
  onAddComment,
  onDeleteComment,
  onManagePostStatus,
  onLikeDislike,
  handleClose,
  currentUser,
  groupName,
  isExpandedPost,
  buildingTimezone = 'America/Chicago',
  isCommentsEnabled,
  readOnlyMode,
  scrollIntoCommentId,
  onApproveRejectCallback,
  isSubmitting,
}) => {
  const isScheduled = !published
  const groupIdValue = typeof groupId === 'object' ? groupId.id : groupId
  const commentRefs = useRef({})

  const [commentState, setCommentState] = useState('')
  const [attachedImages, setAttachedImages] = useState([])
  const [isOpenImagesPicker, setOpenImagesPicker] = useState(false)

  const isLiked = !!(likes && likes.find((like) => like.author.email === currentUser.email))

  const onExpandPost = () => onShowFullPost(id)

  const handleAddComment = async () => {
    const sendData = {
      message: commentState,
      images: isOpenImagesPicker ? attachedImages : [],
    }
    await resetQueries([GET_PENDING_COMMENTS_QUERY_KEY])
    onAddComment(id, sendData)
    setCommentState('')
    setOpenImagesPicker(false)
    setAttachedImages([])
  }

  const getAuthorRole = (role) => {
    switch (role) {
      case 'Administrator':
        return 'Admin'
      case 'PropertyManager':
        return 'PM'
      case 'FrontDesk':
        return 'FD'
      case 'Manager':
        return 'Manager'
      default:
        return null
    }
  }

  const dropdownOptions = (author) => {
    const options = [
      {
        id: 0,
        text: 'Edit',
        icon: 'edit',
        action: () => onEditPost(id),
      },
      {
        id: 1,
        text: 'Remove',
        icon: 'trash',
        action: () => onDeletePost(id),
      },
    ]

    if (!isScheduled && author.email !== currentUser.email) {
      options.shift()
    }
    return options
  }

  const displayComments = isExpandedPost && comments && comments.length > 0
  const commentsCount = groupId.approveComments
    ? comments?.filter((comment) => comment.approved).length
    : comments?.length

  const sortedPendingAndNormalComments = [
    ...sortByDate(comments.filter((comment) => !comment.approved)),
    ...sortByDate(comments.filter((comment) => comment.approved)),
  ]
  const commentsToDisplay = groupId.approveComments
    ? sortedPendingAndNormalComments
    : sortByDate(comments, true)

  useEffect(() => {
    if (!scrollIntoCommentId) return

    const element = commentRefs.current[scrollIntoCommentId]

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [scrollIntoCommentId, comments])

  return (
    <React.Fragment>
      <div className="post-small-card">
        <div className="post-small-card__header">
          <div
            className={`post-small-card__header__content ${!isExpandedPost ? 'clickable' : ''}`}
            onClick={!isExpandedPost ? onExpandPost : null}
          >
            {!isWelcomePost ? (
              <React.Fragment>
                <Avatar size="big">
                  <img
                    src={getAssetLink({ path: 'chitown-avatar/avatar', file: author.avatar })}
                    alt=""
                  />
                </Avatar>
                <div className="post-small-card__header__info">
                  <span className="post-small-card__header__info-text">
                    {`${author.firstName} ${author.lastName}`}&#160;
                    {`${author.unit && author.unit !== USER_DELETED ? `#${author.unit}` : ''}`}
                  </span>
                  <span
                    className={`post-small-card__header__info-note ${isScheduled ? 'scheduled' : ''}`}
                  >
                    {isScheduled
                      ? `Scheduled for ${format(toZonedTime(new Date(scheduledAt), buildingTimezone), 'h:mm a MM/dd/yyyy')}`
                      : formatDistanceToNow(new Date(isScheduled ? scheduledAt : createdAt), {
                          addSuffix: true,
                        })}
                  </span>
                </div>
                {['Administrator', 'PropertyManager'].includes(author.role) && (
                  <div className="author-role-container">{getAuthorRole(author.role)}</div>
                )}
                {groupName && <div className="group-name-container">{groupName}</div>}
              </React.Fragment>
            ) : (
              <div className="post-small-card__is-welcome-post">
                <div className="flex-row">
                  <Avatar size="big">
                    <img
                      src={getAssetLink({ path: 'chitown-avatar/avatar', file: author.avatar })}
                      alt=""
                    />
                  </Avatar>
                  <span className="content-title">
                    {`${author.firstName} ${author.lastName}`}&#160;
                    {`${author.unit && author.unit !== USER_DELETED ? `#${author.unit}` : ''}`}
                  </span>
                  <span className="content-message">{'has joined the group!'}</span>
                </div>
                <span className="create-time">
                  {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
                </span>
              </div>
            )}
          </div>
          <div className="post-small-card__header__right">
            <div className="post-small-card__header__right__flexContainer">
              {(approved || isScheduled) && !readOnlyMode && (
                <DropdownDots options={dropdownOptions(author)} style={{ marginLeft: '12px' }} />
              )}
              {isExpandedPost && (
                <div className="close-button" onClick={handleClose}>
                  <Icon icon="cross" />
                </div>
              )}
            </div>
            {!isExpandedPost && !approved && !isScheduled && (
              <div className="pending-label">Pending approval</div>
            )}
          </div>
        </div>
        {!isWelcomePost && (
          <div
            className={`post-small-card__content ${!isExpandedPost ? 'clickable' : ''}`}
            onClick={!isExpandedPost ? onExpandPost : null}
          >
            <span className="content-title">{isExpandedPost ? title : cutTooLong(title, 100)}</span>
            <span className="content-message">
              {isExpandedPost ? message : cutTooLong(message, 255)}
            </span>
          </div>
        )}
        {!isWelcomePost && images.length > 0 && !isExpandedPost && (
          <div className="post-small-card__images-preview">
            <ImagesContainer images={images} onClick={onExpandPost} />
          </div>
        )}

        {approved && !isScheduled ? (
          <div className="post-small-card__toolbar">
            <div className="post-small-card__toolbar-wrap-buttons">
              <div className="post-small-card__toolbar-button">
                <Icon icon="heart" viewBox={'0 -2 14 18'} fill={'#E03838'} />
                <span>{likes ? `${likes.length}` : '0'}</span>
              </div>
              {!readOnlyMode && (
                <div
                  className="post-small-card__toolbar-button clickable"
                  onClick={() => onLikeDislike(groupIdValue, id, null, isLiked)}
                >
                  <Icon
                    icon="heart"
                    viewBox={'0 -2 14 18'}
                    fill={isLiked ? 'grey' : '#FFFF'}
                    svgProps={{ stroke: 'grey' }}
                  />
                  <span>{isLiked ? 'Dislike it' : 'Like it'}</span>
                </div>
              )}
            </div>
            <div
              className={`post-small-card__toolbar-button ${!isExpandedPost ? 'clickable' : ''}`}
              onClick={!isExpandedPost ? onExpandPost : null}
            >
              <span>{`${commentsCount} Comment${commentsCount === 1 ? '' : 's'}`}</span>
            </div>
          </div>
        ) : null}

        {approved && !isScheduled && (
          <div className="post-small-card__comments">
            {displayComments ? (
              <div className="post-small-card__comments-container">
                {commentsToDisplay.map((comment) => (
                  <PostComment
                    ref={(el) => {
                      commentRefs.current[comment.id] = el
                    }}
                    key={comment.id}
                    currentUser={currentUser}
                    comment={comment}
                    onDeleteComment={() => onDeleteComment(id, comment.id)}
                    onLikeDislike={(commentId, isLiked) =>
                      onLikeDislike(groupIdValue, id, commentId, isLiked)
                    }
                    readOnlyMode={readOnlyMode}
                    postId={id}
                    groupId={groupIdValue}
                    approvedCommentsOn={groupId.approveComments}
                    onApproveRejectCallback={onApproveRejectCallback}
                    isSubmitting={isSubmitting}
                  />
                ))}
              </div>
            ) : null}
          </div>
        )}
        {!isExpandedPost && approved && !isScheduled ? (
          isCommentsEnabled ? (
            <div
              className={`post-small-card__footer ${!isExpandedPost ? 'clickable' : ''}`}
              onClick={!isExpandedPost ? onExpandPost : null}
            >
              <div className="post-small-card__footer__input-container">
                <Avatar size="big">
                  <img
                    src={getAssetLink({ path: 'chitown-avatar/avatar', file: currentUser.avatar })}
                    alt=""
                  />
                </Avatar>
                <div className="post-small-card__footer__input-container__clickable-text-field">
                  Add a comment
                </div>
              </div>
            </div>
          ) : null
        ) : !isExpandedPost && !isScheduled ? (
          <div className="post-small-card__approval">
            <button
              className="button deny-button"
              onClick={() => onManagePostStatus(groupIdValue, id, false)}
            >
              <Icon icon="cross" /> Deny Post
            </button>
            <button
              className="button approve-button"
              onClick={() => onManagePostStatus(groupIdValue, id, true)}
            >
              <Icon icon="checkmark" viewBox={'0 0 29 30'} /> Approve Post
            </button>
          </div>
        ) : null}
      </div>
      {isExpandedPost && approved && !isScheduled ? (
        <div
          className={`post-small-card__footer ${!isExpandedPost ? 'clickable' : ''}`}
          onClick={!isExpandedPost ? onExpandPost : null}
        >
          {isOpenImagesPicker && (
            <div className="post-small-card__footer__images-picker">
              <ImagesPicker
                value={attachedImages}
                imageLimit={5}
                onChange={(images) => setAttachedImages(images)}
              />
            </div>
          )}
          {!readOnlyMode && isCommentsEnabled && (
            <div className="post-small-card__footer__input-container">
              <Avatar size="big">
                <img
                  src={getAssetLink({ path: 'chitown-avatar/avatar', file: currentUser.avatar })}
                  alt=""
                />
              </Avatar>
              <TextField
                name="comment"
                placeholder="Add a comment"
                disabled={!isExpandedPost}
                value={commentState}
                onChange={(e) => setCommentState(e.target.value)}
              />
              <div
                className="post-small-card__footer__input-container__button"
                onClick={() => setOpenImagesPicker(!isOpenImagesPicker)}
              >
                <Icon icon="picture" fill={isOpenImagesPicker ? '#1780c6' : ''} />
              </div>
              <div
                className="post-small-card__footer__input-container__button"
                onClick={handleAddComment}
                disabled={!isExpandedPost}
              >
                <Icon icon="send" />
              </div>
            </div>
          )}
        </div>
      ) : isExpandedPost && !isScheduled ? (
        <div className="post-small-card__approval">
          <button
            className="button deny-button"
            onClick={() => onManagePostStatus(groupIdValue, id, false)}
          >
            <Icon icon="cross" /> Deny Post
          </button>
          <button
            className="button approve-button"
            onClick={() => onManagePostStatus(groupIdValue, id, true)}
          >
            <Icon icon="checkmark" viewBox={'0 0 29 30'} /> Approve Post
          </button>
        </div>
      ) : null}
    </React.Fragment>
  )
}

PostSmallCard.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    message: PropTypes.string,
    createdAt: PropTypes.number,
    isMarketing: PropTypes.bool,
    images: PropTypes.arrayOf(PropTypes.string),
    author: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      avatar: PropTypes.string,
    }),
    comments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        message: PropTypes.string,
      }),
    ),
    scheduledAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onDeletePost: PropTypes.func.isRequired,
  onEditPost: PropTypes.func.isRequired,
  onShowFullPost: PropTypes.func,
  onAddComment: PropTypes.func,
  onDeleteComment: PropTypes.func,
  currentUser: PropTypes.object,
  onManagePostStatus: PropTypes.func,
  onLikeDislike: PropTypes.func,
  handleClose: PropTypes.func,
  groupName: PropTypes.string,
  isCommentsDisabled: PropTypes.bool,
  isExpandedPost: PropTypes.bool,
  readOnlyMode: PropTypes.bool,
  buildingTimezone: PropTypes.string,
  scrollIntoCommentId: PropTypes.number,
}

export default PostSmallCard
